import React from 'react';

/**
 * Popup shown when user tries to navigate away without selecting a compulsory item
 * Matches the Brand Compatibility Issue popup style
 */
export const CompulsorySelectionPopup = ({ 
  pendingCompulsory,
  onStayHere,
  onClose 
}) => {
  if (!pendingCompulsory) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-[#1a1a2e]/95 backdrop-blur-xl rounded-[20px] w-full max-w-md shadow-2xl border border-white/10">
        {/* Header with warning icon */}
        <div className="p-6 text-center">
          <div className="w-14 h-14 mx-auto mb-4 bg-orange-500/20 rounded-full flex items-center justify-center">
            <span className="text-3xl">⚠️</span>
          </div>
          <h3 className="text-white font-semibold text-xl mb-2">Selection Required</h3>
          <p className="text-gray-400 text-sm">
            Please select a <span className="text-orange-400 font-medium">{pendingCompulsory.name}</span> before 
            navigating to another section.
          </p>
        </div>

        {/* Info box */}
        <div className="px-6 pb-4">
          <div className="bg-white/5 rounded-xl p-4 border border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-orange-400 text-lg font-bold">!</span>
              </div>
              <div>
                <div className="text-white font-medium">{pendingCompulsory.name}</div>
                <div className="text-gray-500 text-sm">
                  {pendingCompulsory.type === 'category' ? 'Required Category' : 'Required Subcategory'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="p-6 pt-2 space-y-3">
          <button 
            onClick={onStayHere}
            className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3 rounded-xl font-medium transition-colors"
          >
            Select {pendingCompulsory.name}
          </button>
          <button 
            onClick={onClose}
            className="w-full text-gray-400 hover:text-white py-2 text-sm transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Popup shown when user tries to submit with missing compulsory items
 */
export const MissingCompulsoryPopup = ({ 
  missingItems, 
  onNavigateToCategory, 
  onClose 
}) => (
  <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
    <div className="bg-[#1a1a2e]/95 backdrop-blur-xl rounded-[20px] w-full max-w-md shadow-2xl border border-white/10 max-h-[85vh] flex flex-col">
      {/* Header with warning icon */}
      <div className="p-6 text-center">
        <div className="w-14 h-14 mx-auto mb-4 bg-orange-500/20 rounded-full flex items-center justify-center">
          <span className="text-3xl">⚠️</span>
        </div>
        <h3 className="text-white font-semibold text-xl mb-2">Complete Your Build</h3>
        <p className="text-gray-400 text-sm">
          Please select the following required items before submitting:
        </p>
      </div>
      
      {/* Missing items list */}
      <div className="px-6 pb-4 flex-1 overflow-y-auto">
        <div className="space-y-2">
          {missingItems.map((item, index) => (
            <button
              key={index}
              onClick={() => {
                onNavigateToCategory(item.name, item.type === 'subcategory' ? item.name : '');
                onClose();
              }}
              className="w-full bg-white/5 hover:bg-white/10 rounded-xl p-4 border border-white/10 hover:border-orange-500/50 transition-all text-left group"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center">
                    <span className="text-orange-400 text-lg font-bold">!</span>
                  </div>
                  <div>
                    <div className="text-white font-medium">{item.name}</div>
                    <div className="text-gray-500 text-sm">
                      {item.type === 'category' ? 'Required Category' : 'Required Subcategory'}
                    </div>
                  </div>
                </div>
                <div className="text-orange-400 text-sm font-medium group-hover:translate-x-1 transition-transform">
                  Select →
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
      
      {/* Footer */}
      <div className="p-6 pt-2">
        <button 
          onClick={onClose}
          className="w-full text-gray-400 hover:text-white py-2 text-sm transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
);

/**
 * Prominent notification shown when user needs to select a compulsory item
 * Positioned over product image area - responsive for mobile and desktop
 * Supports shake animation when user tries blocked action
 * Can be dismissed but reappears when user tries to navigate away
 */
export const CompulsoryBanner = ({
  pendingCompulsory,
  shake,
  dismissed,
  onDismiss
}) => {
  if (!pendingCompulsory) return null;

  // Don't show if dismissed, unless shaking (user tried to navigate)
  if (dismissed && !shake) return null;

  return (
    <>
      {/* Mobile: Positioned over product image at top of content area */}
      <div className="lg:hidden absolute left-0 right-0 top-24 flex justify-center z-40 pointer-events-none px-4">
        <div className={`pointer-events-auto ${shake ? 'animate-shake' : ''}`}>
          <div className={`relative bg-[#1a1a2e]/95 backdrop-blur-xl text-white px-4 py-3 pr-10 rounded-xl shadow-2xl border flex items-center gap-3 transition-all duration-200 ${shake ? 'border-orange-500 shadow-orange-500/20' : 'border-white/10'}`}>
            {/* Close button */}
            <button
              onClick={onDismiss}
              className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center text-gray-400 hover:text-white transition-colors rounded-full hover:bg-white/10"
              aria-label="Close"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div className={`w-10 h-10 bg-orange-500/20 rounded-full flex items-center justify-center flex-shrink-0 ${shake ? 'animate-pulse' : ''}`}>
              <svg className="w-5 h-5 text-orange-400" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2L1 21h22L12 2zm0 3.99L19.53 19H4.47L12 5.99zM11 10v4h2v-4h-2zm0 6v2h2v-2h-2z"/>
              </svg>
            </div>
            <div>
              <div className="font-semibold text-sm">Selection Required</div>
              <div className="text-gray-400 text-xs">
                Please select a <span className="text-white font-medium">{pendingCompulsory.name}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop: Centered with sidebar offset */}
      <div className="hidden lg:flex fixed inset-0 items-center justify-center z-40 pointer-events-none" style={{ paddingRight: '320px' }}>
        <div className={`pointer-events-auto ${shake ? 'animate-shake' : ''}`}>
          <div className={`relative bg-[#1a1a2e]/95 backdrop-blur-xl text-white px-8 py-5 pr-12 rounded-2xl shadow-2xl border flex items-center gap-5 transition-all duration-200 ${shake ? 'border-orange-500 shadow-orange-500/20' : 'border-white/10'}`}>
            {/* Close button */}
            <button
              onClick={onDismiss}
              className="absolute top-3 right-3 w-6 h-6 flex items-center justify-center text-gray-400 hover:text-white transition-colors rounded-full hover:bg-white/10"
              aria-label="Close"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div className={`w-12 h-12 bg-orange-500/20 rounded-full flex items-center justify-center flex-shrink-0 ${shake ? 'animate-pulse' : ''}`}>
              <svg className="w-6 h-6 text-orange-400" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2L1 21h22L12 2zm0 3.99L19.53 19H4.47L12 5.99zM11 10v4h2v-4h-2zm0 6v2h2v-2h-2z"/>
              </svg>
            </div>
            <div>
              <div className="font-semibold text-lg">Selection Required</div>
              <div className="text-gray-400">
                Please select a <span className="text-white font-medium">{pendingCompulsory.name}</span> to continue
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slideDown {
          animation: slideDown 0.3s ease-out;
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-6px); }
          20%, 40%, 60%, 80% { transform: translateX(6px); }
        }
        .animate-shake {
          animation: shake 0.5s ease-in-out;
        }
      `}</style>
    </>
  );
};

/**
 * Small indicator for category buttons showing compulsory status
 */
export const CompulsoryIndicator = ({ status }) => {
  if (!status.isCompulsory) return null;

  if (status.isPending) {
    return (
      <span className="absolute -top-1 -right-1 w-4 h-4 bg-orange-500 rounded-full flex items-center justify-center animate-pulse">
        <span className="text-white text-[8px] font-bold">!</span>
      </span>
    );
  }

  if (status.isEmpty) {
    return (
      <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full" title="Required">
        <span className="sr-only">Required</span>
      </span>
    );
  }

  // Has selection - show checkmark
  return (
    <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full flex items-center justify-center">
      <span className="text-white text-[8px]">✓</span>
    </span>
  );
};

export default MissingCompulsoryPopup;